import { useState } from 'react';

export default function ProductTabs() {
  const [activeTab, setActiveTab] = useState<'detail' | 'specs' | 'reviews'>('detail');

  return (
    <div>
      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <div className="flex gap-8">
          <button
            onClick={() => setActiveTab('detail')}
            className={`pb-4 px-2 font-medium transition-colors whitespace-nowrap cursor-pointer ${
              activeTab === 'detail'
                ? 'text-teal-600 border-b-2 border-teal-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            상품 상세
          </button>
          <button
            onClick={() => setActiveTab('specs')}
            className={`pb-4 px-2 font-medium transition-colors whitespace-nowrap cursor-pointer ${
              activeTab === 'specs'
                ? 'text-teal-600 border-b-2 border-teal-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            제품 사양
          </button>
          <button
            onClick={() => setActiveTab('reviews')}
            className={`pb-4 px-2 font-medium transition-colors whitespace-nowrap cursor-pointer ${
              activeTab === 'reviews'
                ? 'text-teal-600 border-b-2 border-teal-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            구매 후기
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="py-8">
        {activeTab === 'detail' && <DetailTab />}
        {activeTab === 'specs' && <SpecsTab />}
        {activeTab === 'reviews' && <ReviewsTab />}
      </div>
    </div>
  );
}

function DetailTab() {
  return (
    <div className="prose max-w-none">
      <div className="space-y-8">
        <section>
          <h3 className="text-2xl font-bold text-gray-900 mb-4">NVIDIA GeForce RTX 4070 - 차세대 게이밍의 시작</h3>
          <p className="text-gray-700 leading-relaxed">
            NVIDIA GeForce RTX 4070은 최신 Ada Lovelace 아키텍처를 기반으로 한 고성능 그래픽카드입니다. 
            12GB GDDR6X 메모리와 5,888개의 CUDA 코어를 탑재하여 4K 게이밍과 크리에이터 작업에 최적화된 성능을 제공합니다.
          </p>
        </section>

        <section className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-8 border border-teal-100">
          <h4 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
            <i className="ri-flashlight-line text-teal-600 mr-3"></i>
            주요 특징
          </h4>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-teal-100">
              <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-speed-up-line text-2xl text-white"></i>
              </div>
              <h5 className="font-bold text-gray-900 mb-2">DLSS 3 기술</h5>
              <p className="text-sm text-gray-600">AI 기반 프레임 생성으로 최대 4배 성능 향상. 더 높은 프레임률로 부드러운 게이밍 경험을 제공합니다.</p>
            </div>
            <div className="bg-white rounded-xl p-6 border border-teal-100">
              <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-sun-line text-2xl text-white"></i>
              </div>
              <h5 className="font-bold text-gray-900 mb-2">레이 트레이싱 3세대</h5>
              <p className="text-sm text-gray-600">실시간 광선 추적으로 사실적인 조명, 반사, 그림자 효과를 구현합니다.</p>
            </div>
            <div className="bg-white rounded-xl p-6 border border-teal-100">
              <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-video-line text-2xl text-white"></i>
              </div>
              <h5 className="font-bold text-gray-900 mb-2">AV1 인코딩</h5>
              <p className="text-sm text-gray-600">최신 AV1 코덱 지원으로 고품질 스트리밍과 녹화가 가능합니다.</p>
            </div>
            <div className="bg-white rounded-xl p-6 border border-teal-100">
              <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-temp-cold-line text-2xl text-white"></i>
              </div>
              <h5 className="font-bold text-gray-900 mb-2">효율적인 냉각</h5>
              <p className="text-sm text-gray-600">트리플 팬 쿨링 시스템으로 최적의 온도를 유지하며 조용한 작동을 보장합니다.</p>
            </div>
          </div>
        </section>

        <section>
          <h4 className="text-xl font-bold text-gray-900 mb-4">추천 대상</h4>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <i className="ri-gamepad-line text-3xl text-teal-600 mb-3"></i>
              <h5 className="font-bold text-gray-900 mb-2">게이머</h5>
              <p className="text-sm text-gray-600">4K 해상도에서 최신 AAA 게임을 고프레임으로 즐기고 싶은 게이머</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <i className="ri-palette-line text-3xl text-teal-600 mb-3"></i>
              <h5 className="font-bold text-gray-900 mb-2">크리에이터</h5>
              <p className="text-sm text-gray-600">영상 편집, 3D 렌더링, AI 작업을 수행하는 전문 크리에이터</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <i className="ri-live-line text-3xl text-teal-600 mb-3"></i>
              <h5 className="font-bold text-gray-900 mb-2">스트리머</h5>
              <p className="text-sm text-gray-600">고품질 게임 방송과 녹화를 동시에 진행하는 스트리머</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function SpecsTab() {
  const specs = [
    { label: '제품명', value: 'NVIDIA GeForce RTX 4070' },
    { label: 'GPU 아키텍처', value: 'Ada Lovelace' },
    { label: 'CUDA 코어', value: '5,888개' },
    { label: '메모리 용량', value: '12GB GDDR6X' },
    { label: '메모리 인터페이스', value: '192-bit' },
    { label: '메모리 대역폭', value: '504 GB/s' },
    { label: '베이스 클럭', value: '1,920 MHz' },
    { label: '부스트 클럭', value: '2,475 MHz' },
    { label: 'RT 코어', value: '3세대 46개' },
    { label: 'Tensor 코어', value: '4세대 184개' },
    { label: 'TDP', value: '200W' },
    { label: '권장 파워', value: '650W 이상' },
    { label: '전원 커넥터', value: '16-pin (12VHPWR)' },
    { label: '출력 포트', value: 'DisplayPort 1.4a x3, HDMI 2.1a x1' },
    { label: '최대 해상도', value: '7680 x 4320 (8K)' },
    { label: 'DirectX 지원', value: 'DirectX 12 Ultimate' },
    { label: 'PCIe 인터페이스', value: 'PCIe 4.0 x16' },
    { label: '카드 크기', value: '304mm x 137mm x 61mm (3슬롯)' },
    { label: '제조사 보증', value: '3년' }
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <table className="w-full">
        <tbody>
          {specs.map((spec, index) => (
            <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
              <td className="px-6 py-4 font-medium text-gray-900 w-1/3">{spec.label}</td>
              <td className="px-6 py-4 text-gray-700">{spec.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReviewsTab() {
  const reviews = [
    {
      id: 1,
      author: '김게이머',
      rating: 5,
      date: '2025-01-15',
      content: '4K 게이밍이 정말 부드럽습니다! DLSS 3 덕분에 사이버펑크 2077도 60fps 이상으로 쾌적하게 플레이하고 있어요. 발열도 생각보다 낮고 소음도 거의 없습니다.',
      helpful: 24
    },
    {
      id: 2,
      author: '박크리에이터',
      rating: 5,
      date: '2025-01-12',
      content: '영상 편집 작업이 정말 빨라졌어요. 다빈치 리졸브에서 4K 타임라인도 끊김 없이 작업 가능합니다. 렌더링 시간도 이전 GPU 대비 절반으로 줄었습니다.',
      helpful: 18
    },
    {
      id: 3,
      author: '이스트리머',
      rating: 5,
      date: '2025-01-10',
      content: 'AV1 인코딩 지원이 정말 좋네요. 트위치 방송하면서 게임도 최고 옵션으로 돌릴 수 있어요. 듀얼 모니터 환경에서도 성능 저하 없습니다.',
      helpful: 15
    },
    {
      id: 4,
      author: '최프로게이머',
      rating: 4,
      date: '2025-01-08',
      content: '경쟁 게임용으로 구매했는데 만족합니다. 1440p에서 240fps 이상 나와서 모니터 성능을 완전히 활용할 수 있어요. 가격 대비 성능이 훌륭합니다.',
      helpful: 12
    },
    {
      id: 5,
      author: '정디자이너',
      rating: 5,
      date: '2025-01-05',
      content: '블렌더 작업할 때 OptiX 렌더링 속도가 정말 빠릅니다. 12GB VRAM도 넉넉해서 복잡한 씬도 문제없이 처리되네요. 3D 작업자에게 강력 추천합니다!',
      helpful: 20
    }
  ];

  const averageRating = 4.8;
  const totalReviews = reviews.length;

  return (
    <div className="space-y-8">
      {/* Rating Summary */}
      <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-8 border border-teal-100">
        <div className="flex items-center gap-8">
          <div className="text-center">
            <div className="text-5xl font-bold text-gray-900 mb-2">{averageRating}</div>
            <div className="flex items-center justify-center gap-1 mb-2">
              {[...Array(5)].map((_, i) => (
                <i key={i} className={`${i < Math.floor(averageRating) ? 'ri-star-fill text-yellow-400' : 'ri-star-line text-gray-300'}`}></i>
              ))}
            </div>
            <div className="text-sm text-gray-600">{totalReviews}개 후기</div>
          </div>
          <div className="flex-1">
            <div className="space-y-2">
              {[5, 4, 3, 2, 1].map((star) => {
                const count = reviews.filter(r => r.rating === star).length;
                const percentage = (count / totalReviews) * 100;
                return (
                  <div key={star} className="flex items-center gap-3">
                    <span className="text-sm text-gray-600 w-12">{star}점</span>
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-yellow-400 h-2 rounded-full transition-all"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-gray-600 w-12 text-right">{count}개</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Reviews List */}
      <div className="space-y-6">
        {reviews.map((review) => (
          <div key={review.id} className="bg-white border border-gray-200 rounded-xl p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="font-bold text-gray-900">{review.author}</span>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className={`text-sm ${i < review.rating ? 'ri-star-fill text-yellow-400' : 'ri-star-line text-gray-300'}`}></i>
                    ))}
                  </div>
                </div>
                <div className="text-sm text-gray-500">{review.date}</div>
              </div>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">{review.content}</p>
            <button className="text-sm text-gray-600 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-thumb-up-line mr-1"></i>
              도움이 돼요 ({review.helpful})
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}